import { AnimatePresence, motion } from 'motion/react'
import { type ReactElement, useRef, useState } from 'react'
import { getToaster, type ToastId } from './getToaster'
import { Toast, type ToastProps } from './Toast'
import { toastStack } from './Toast.css'
import { ToastType } from './ToastType'

export const ToastStack = (): ReactElement => {
  const [toasts, setToasts] = useState<Array<ToastProps & { id: string }>>([])
  const timeoutById = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const removeToast = (id: ToastId) => {
    const t = timeoutById.current.get(id)
    if (t) {
      clearTimeout(t)
      timeoutById.current.delete(id)
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  getToaster().dismiss = (toastId: ToastId) => {
    removeToast(toastId)
  }

  getToaster().current = (
    title?: string,
    content?: string,
    type?: ToastType,
    durationMs = 5000
  ) => {
    const id = Math.random().toString(36)
    if (
      toasts.some((toast) => toast.title === title && toast.content === content)
    ) {
      return false
    }
    setToasts((prev) => {
      const timeout = setTimeout(() => removeToast(id), durationMs)
      timeoutById.current.set(id, timeout)
      return [...prev, { title, content, type, id }]
    })
    return id
  }

  return (
    <div className={toastStack}>
      <AnimatePresence>
        {toasts.map(({ title, content, type, id }) => (
          <motion.div
            key={id}
            initial={{ opacity: 0, y: 30, zIndex: 999, position: 'relative' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30, zIndex: 999, position: 'relative' }}
          >
            <Toast
              key={id}
              type={type ?? ToastType.Default}
              title={title ?? ''}
              content={content ?? ''}
              onClose={() => removeToast(id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
